import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { UncontrolledPopover, PopoverHeader, PopoverBody } from 'reactstrap';
import { useParams } from 'react-router-dom';
import _ from 'underscore';

import services from 'api/services';
import { Button } from 'components/Button/RoundButton';
import { useActions } from 'hooks/useActions';
import * as actions from './actions';
import {
  QuestionAndOptionsWrapper,
  QuestionNumber,
  QuestionSection,
  OptionsContainer,
  NavigationButtonsComponent,
  QuestionValue,
  OptionBox,
  OptionValue,
  ParagraphComponent,
  ParagraphAndQuestionCountComponent,
  OptionChar,
  ParagraphButtonContainer,
} from './styles';
import MultichoiceCreatorQuillEditor from 'pages/MultichoiceCreator/MultichoiceCreatorQuillEditor';
import { questionTypes } from 'constants/tests';

const { InputService } = services;

export default React.memo(() => {
  const { ticketId } = useParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hideSkipInfo, setHideSkipInfo] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdaptiveTest, setIsAdaptiveTest] = useState(true);
  const [paragraphQuestion, setParagraphQuestion] = useState({});

  const questionRef = useRef(null);
  const optionsRef = useRef(null);
  const questionIdRef = useRef(null);

  const { updateInput, setCurrentQuestionObject, setCurrentOptionId, setCurrentTextAnswer } = useActions(actions);
  const { ticketResponse, loading, currentQuestionObject, currentOptionId, nonAdaptiveQuestionObject, textAnswer } =
    useSelector((state) => state.MultichoiceTestPageReducer);
  const { ticket, test } = ticketResponse;

  useEffect(() => {
    if (!ticketId) return;
    const updateBlurTime = () =>
      InputService.updateEvent(ticketId, questionIdRef.current, { isTimeAwayEvent: true, event: 'blur' });

    const updateFocusTime = () =>
      InputService.updateEvent(ticketId, questionIdRef.current, { isTimeAwayEvent: true, event: 'focus' });

    window.addEventListener('focus', updateFocusTime);
    window.addEventListener('blur', updateBlurTime);

    return () => {
      window.removeEventListener('focus', updateFocusTime);
      window.removeEventListener('blur', updateBlurTime);
    };
  }, [ticketId]);

  useEffect(() => {
    setIsAdaptiveTest(test.adaptiveTest);
    setParagraphQuestion(ticket.paragraphWiseQuestion);
  }, [ticketResponse, test.adaptiveTest, ticket.paragraphWiseQuestion]);

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  useEffect(() => {
    setCurrentTextAnswer(
      localStorage.getItem(`fsFreeText-${currentQuestionObject.question._id}`) ??
        currentQuestionObject.question.textAnswer ??
        ''
    );
  }, [currentQuestionObject.question, setCurrentTextAnswer]);

  useEffect(() => {}, [currentQuestionObject]);

  useEffect(() => {
    if (!ticketId) return;

    // If not adaptive and left previous question, notify backend so we can update timeSpentOnThisQuestionInMs
    if (!isAdaptiveTest && questionIdRef.current && questionIdRef.current !== currentQuestionObject?.question?._id)
      InputService.updateEvent(ticketId, questionIdRef.current, { leftQuestion: true });

    questionIdRef.current = currentQuestionObject.question._id;
  }, [currentQuestionObject, isAdaptiveTest, ticketId]);

  useEffect(() => {
    if (!currentQuestionObject) return;

    const updateCopiedQuestionId = () => {
      InputService.updateEvent(ticketId, currentQuestionObject.question._id, { question: true });
    };
    const updateCopiedOptionId = (optionId) => {
      InputService.updateEvent(ticketId, currentQuestionObject.question._id, { option: optionId });
    };
    const updateRightClickEvent = (event) => {
      if (event.target.tagName === 'IMG')
        InputService.updateEvent(ticketId, currentQuestionObject.question._id, { isRightClickOnImage: true });
      else InputService.updateEvent(ticketId, currentQuestionObject.question._id, { isRightClick: true });
    };

    const stripStringForCopyCompare = (htmlString) =>
      htmlString
        .replace(/<[^>]*>|\s+/g, '')
        .toString()
        .trim();

    const checkSelectedText = () => {
      let selectedText = stripStringForCopyCompare(window.getSelection().toString());
      if (!selectedText) return;
      let questionValue = stripStringForCopyCompare(currentQuestionObject.question.value);
      let optionsSelected = [];
      // Use for in loop and _.size since currentQuestionObject.options can be array or object depending on adaptive or not
      for (const optionKey in currentQuestionObject.options) {
        const option = currentQuestionObject.options[optionKey];
        let currentOption = stripStringForCopyCompare(option.value);
        if (selectedText.includes(currentOption)) optionsSelected.push(option._id);
      }
      if (selectedText.includes(questionValue) && optionsSelected.length === _.size(currentQuestionObject.options))
        return InputService.updateEvent(ticketId, currentQuestionObject.question._id, { isAllTextCopied: true });

      if (selectedText.includes(questionValue)) updateCopiedQuestionId();
      for (const optionId of optionsSelected) updateCopiedOptionId(optionId);
    };

    setIsExpanded(false);

    const questionElement = questionRef.current;
    questionElement.addEventListener('copy', updateCopiedQuestionId);
    window.addEventListener('copy', checkSelectedText);
    window.addEventListener('contextmenu', updateRightClickEvent);
    optionsRef?.current?.forEach((optionElement) =>
      optionElement.addEventListener('copy', () => updateCopiedOptionId(optionElement.id))
    );

    return () => {
      questionElement.removeEventListener('copy', updateCopiedQuestionId);
      optionsRef?.current?.forEach((optionElement) =>
        optionElement?.removeEventListener('copy', () => updateCopiedOptionId(optionElement.id))
      );
      window.removeEventListener('copy', checkSelectedText);
      window.removeEventListener('contextmenu', updateRightClickEvent);
    };
  }, [currentQuestionObject, ticketId]);

  const getMapOfOptionsRef = () => {
    if (!optionsRef.current) optionsRef.current = new Map();
    return optionsRef.current;
  };

  const getParagraph = () => {
    return (
      <>
        {!isAdaptiveTest && (
          <ParagraphAndQuestionCountComponent>
            <b>Paragraph: {paragraphQuestion[currentQuestionObject.paragraph._id]['paraNumber']}</b>
            <b>
              Question:
              {paragraphQuestion[currentQuestionObject.paragraph._id][currentQuestionObject.question._id]}/
              {paragraphQuestion[currentQuestionObject.paragraph._id]['count']}
            </b>
          </ParagraphAndQuestionCountComponent>
        )}
        <ParagraphComponent
          dangerouslySetInnerHTML={{
            __html: currentQuestionObject.paragraph.value.substring(
              0,
              isExpanded ? currentQuestionObject.paragraph.value.length : 700
            ),
          }}
        ></ParagraphComponent>
        {currentQuestionObject.paragraph.value.length > 700 && (
          <ParagraphButtonContainer>
            <Button small primaryBlue primaryGreenBorder removeMargin onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? 'Show less' : 'Show more'}
            </Button>
          </ParagraphButtonContainer>
        )}
      </>
    );
  };

  const getOptionChar = (index) => {
    // CharCode is to get a), b) etc for all options
    return <OptionChar>{`${String.fromCharCode(97 + index)})`}</OptionChar>;
  };

  const getOptions = () => {
    if (!currentQuestionObject) return;
    if (currentQuestionObject.question.questionType === questionTypes.FREE_TEXT)
      return (
        <MultichoiceCreatorQuillEditor
          key={`quill-answer${currentQuestionObject.question._id}`}
          id={`quill-answer${currentQuestionObject.question._id}`}
          placeholder="Type your answer here"
          disabled={false}
          minHeight="200px"
          maxHeight="350px"
          value={textAnswer}
          isComment
          onChange={(updatedValue, delta, source) => {
            if (source !== 'api') {
              localStorage.setItem(`fsFreeText-${currentQuestionObject.question._id}`, updatedValue);
              if (updatedValue) setCurrentTextAnswer(updatedValue);
            }
          }}
        />
      );
    return Object.values(currentQuestionObject.options).map((option, index) => {
      return (
        <OptionBox
          key={option._id}
          isSelected={option.isAnswered || currentOptionId === option._id}
          onClick={() => updateOption(option)}
        >
          {getOptionChar(index)}
          <OptionValue
            isSelected={option.isAnswered || currentOptionId === option._id}
            id={option._id}
            ref={(node) => {
              const map = getMapOfOptionsRef();
              if (node) map.set(option._id, node);
              else map.delete(option._id);
            }}
            dangerouslySetInnerHTML={{ __html: option.value }}
          ></OptionValue>
        </OptionBox>
      );
    });
  };

  // We send the backend request here for non adaptive test, when you click on an option
  // for adaptive tests we do nothing here
  const updateOption = (option) => {
    setCurrentOptionId(option._id);
    if (!isAdaptiveTest) {
      let input = {
        question: currentQuestionObject.question._id,
        option: option._id,
      };
      updateInput(ticketId, input, isAdaptiveTest);
      if (currentQuestionObject.questionNumber !== ticket.totalQuestions) {
        setCurrentQuestionObject(
          Object.values(nonAdaptiveQuestionObject).find(
            (element) => element.questionNumber === currentQuestionObject.questionNumber + 1
          )
        );
      } else {
        setCurrentQuestionObject(null);
      }
    }
  };

  const skipQuestion = () => {
    if (localStorage.getItem(`skipButtonPopup${ticketId}`)) {
      setHideSkipInfo(true);
      updateQuestionInput();
    } else {
      localStorage.setItem(`skipButtonPopup${ticketId}`, 'true');
      setHideSkipInfo(false);
    }
  };

  // We send the backend request here
  // This is used when skipping or clicking next
  const updateQuestionInput = () => {
    if (isAdaptiveTest) {
      let input = {
        question: currentQuestionObject.question._id,
        option: currentOptionId,
        textAnswer: textAnswer,
      };
      updateInput(ticketId, input, isAdaptiveTest);
    } else {
      if (textAnswer) {
        let input = {
          question: currentQuestionObject.question._id,
          textAnswer: textAnswer,
        };
        updateInput(ticketId, input, isAdaptiveTest);
      }
      setCurrentQuestionObject(
        Object.values(nonAdaptiveQuestionObject).find(
          (element) => element.questionNumber === currentQuestionObject.questionNumber + 1
        )
      );
    }
  };

  const setPreviousQuestion = () => {
    setCurrentQuestionObject(
      Object.values(nonAdaptiveQuestionObject).find(
        (element) => element.questionNumber === currentQuestionObject.questionNumber - 1
      )
    );
  };

  const renderNavigationButtons = () => {
    return (
      <NavigationButtonsComponent>
        {isAdaptiveTest ? (
          currentQuestionObject.questionNumber !== ticket.totalQuestions && (
            <>
              <Button
                id="PopoverLegacy"
                zIndex="1"
                primaryBlue
                primaryGreenBorder
                removeMargin
                disabled={currentOptionId || isLoading}
                onBlur={() => setHideSkipInfo(true)}
                onClick={() => skipQuestion()}
              >
                <i className="fa fa-arrow-right">&nbsp;</i>
                Skip Question
              </Button>
              <UncontrolledPopover isOpen={!hideSkipInfo} trigger="legacy" placement="top" target="PopoverLegacy">
                <PopoverHeader>Warning</PopoverHeader>
                <PopoverBody>
                  Once you skip the question, you won't be allowed to return back to a previous question
                </PopoverBody>
              </UncontrolledPopover>
            </>
          )
        ) : (
          <Button
            zIndex="1"
            primaryBlue
            primaryGreenBorder
            removeMargin
            disabled={currentQuestionObject.questionNumber === 1}
            onClick={() => setPreviousQuestion()}
          >
            <i className="fa fa-arrow-left">&nbsp;</i>Previous
          </Button>
        )}
        {currentQuestionObject.questionNumber !== ticket.totalQuestions && (
          <Button
            zIndex="1"
            primaryBlue
            primaryGreenBorder
            removeMargin
            disabled={
              isLoading ||
              (isAdaptiveTest && currentQuestionObject.question.questionType === questionTypes.ONE_CORRECT_ANSWER
                ? !currentOptionId
                : false)
            }
            onClick={() => updateQuestionInput()}
          >
            <i className="fa fa-arrow-right">&nbsp;</i>Next
          </Button>
        )}
      </NavigationButtonsComponent>
    );
  };

  return (
    <>
      {currentQuestionObject.paragraph && getParagraph()}
      <QuestionNumber>Question: {currentQuestionObject.questionNumber}</QuestionNumber>
      <QuestionAndOptionsWrapper id="questionOptionSection">
        <QuestionSection>
          <QuestionValue
            ref={questionRef}
            dangerouslySetInnerHTML={{
              __html: currentQuestionObject.question.value,
            }}
          ></QuestionValue>
        </QuestionSection>
        <OptionsContainer>{getOptions()}</OptionsContainer>
      </QuestionAndOptionsWrapper>
      {renderNavigationButtons()}
    </>
  );
});
